/** Fixed Status Configuration column order. Unknown names sort after these; blank names last. */
export const PASS_GROUP_ORDER = [
  'root',
  'hot',
  'hot pass',
  'pulse',
  'fill',
  'last fill',
  'strip',
  'cap',
  'weave',
] as const;

/** Blank / missing pass names sort after known and unknown named passes. */
export const PASS_GROUP_BLANK_ORDER_INDEX = PASS_GROUP_ORDER.length + 1;

const nameContainsPassKey = (name: string, key: string): boolean => {
  const lower = name.trim().toLowerCase();
  const keyLower = key.toLowerCase();
  if (lower === keyLower) return true;

  // Whole-word / phrase contains (e.g. "Hot Side", "PULSE", "Root CW")
  const escaped = keyLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
  return new RegExp(`(?:^|[^a-z0-9])${escaped}(?:[^a-z0-9]|$)`, 'i').test(lower);
};

export const isBlankPassName = (name: string | null | undefined): boolean =>
  name == null || String(name).trim() === '';

export const getPassGroupOrderIndex = (name: string | null | undefined): number => {
  if (isBlankPassName(name)) return PASS_GROUP_BLANK_ORDER_INDEX;

  const lower = String(name).trim().toLowerCase();
  const exact = PASS_GROUP_ORDER.indexOf(lower as (typeof PASS_GROUP_ORDER)[number]);
  if (exact !== -1) return exact;

  // Longer keys first so "hot pass" / "last fill" win over "hot" / "fill"
  const bySpecificity = PASS_GROUP_ORDER.map((key, index) => ({ key, index })).sort(
    (x, y) => y.key.length - x.key.length
  );

  for (const { key, index } of bySpecificity) {
    if (nameContainsPassKey(lower, key)) return index;
  }

  return PASS_GROUP_ORDER.length;
};

/** Natural name compare; blank/null names always sort last. Does not drop rows. */
export const comparePassNames = (
  a: string | null | undefined,
  b: string | null | undefined
): number => {
  const blankA = isBlankPassName(a);
  const blankB = isBlankPassName(b);
  if (blankA && blankB) return 0;
  if (blankA) return 1;
  if (blankB) return -1;

  const orderCmp = getPassGroupOrderIndex(a) - getPassGroupOrderIndex(b);
  if (orderCmp !== 0) return orderCmp;

  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

/** Sort pass group names for Status Configuration columns. Does not add/remove names. */
export const sortPassGroupsForStatusConfig = (passGroups: Iterable<string>): string[] =>
  Array.from(passGroups).sort((a, b) => comparePassNames(a, b));
